import { focus } from '../../helpers'
import { ThemeTokens } from '../../theming'
import { StyleFn } from '..'

/**
 * reusable reset styles for "button-like" components
 */
export const buttonReset = {
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  backgroundColor: 'transparent',
  textTransform: 'none',
  textDecoration: 'none',
  fontFamily: 'inherit',
  // take precedent over the style of a native <a /> element
  color: 'inherit',
  // disable the focus ring for mouse, touch and keyboard users
  outline: 0,
  border: 0,
  margin: 0, // Remove margin in Safari
  padding: 0, // Remove padding in Firefox
  WebkitTapHighlightColor: 'transparent', // Remove grey highlight
  MozAppearance: 'none',
  WebkitAppearance: 'none',
  display: 'inline-flex',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  verticalAlign: 'middle',
}

/**
 * Reusable styles for "button-like" components
 * sharing the same interactives states as button variants
 * @param theme
 * @param variant
 */
export const buttonVariant = (
  theme: ThemeTokens,
  variant: 'primary' | 'primaryReverse' | 'secondary' | 'ghost' | 'ghostReverse' | 'success' | 'warning' | 'error',
) => {
  const { color } = theme
  let finalVariant = variant
  if (color.type === 'dark') {
    if (variant === 'primary') finalVariant = 'primaryReverse'
    else if (variant === 'ghost') finalVariant = 'ghostReverse'
  }
  const variantColors = color.background.interactive[finalVariant]
  const { default: base, hover, active, contrastText } = variantColors
  return {
    color: contrastText,
    backgroundColor: base,
    '&:hover': {
      backgroundColor: hover,
    },
    '&:active': {
      backgroundColor: active,
    },
    "&[aria-pressed='true']": {
      backgroundColor: active,
    },
  }
}

export const Button: StyleFn = (theme) => {
  const { color, font, opacity, size } = theme
  const { focusWithPseudo } = focus(theme)

  return {
    root: {
      extend: [buttonReset],
      minWidth: `${size.spacing[32]}`,
      minHeight: `${size.spacing[24]}`,
      fontSize: font.size.small,
      lineHeight: font.lineheight.medium,
      padding: `calc(${size.spacing[8]} - 1px) ${size.spacing[16]}`,
      fontWeight: font.weight.bold,
      borderRadius: '3px',
      '& svg, & i': {
        marginLeft: `calc(-${size.spacing[2]})`,
        marginRight: size.spacing[8],
        width: size.spacing[16],
        height: size.spacing[16],
        fontSize: size.spacing[16],
        fill: 'currentColor',
        display: 'inline-flex',
      },
      '&:disabled': {
        opacity: opacity.disabled,
        pointerEvents: 'none',
      },
    },
    // sizes
    _xsmall: {
      minWidth: size.spacing[16],
      fontSize: font.size.small,
      lineHeight: font.lineheight.xxsmall,
      padding: `0 ${size.spacing[8]}`,
    },
    _small: {
      minWidth: size.spacing[32],
      fontSize: font.size.small,
      lineHeight: font.lineheight.medium,
      padding: `calc(${size.spacing[2]} + 1px) ${size.spacing[16]}`,
    },
    _medium: {},
    _large: {
      minWidth: size.spacing[48],
      fontSize: font.size.medium,
      lineHeight: font.lineheight.medium,
      padding: `calc(${size.spacing[12]} - 1px) ${size.spacing[24]}`,
      '& svg, & i': {
        width: size.spacing[24],
        height: size.spacing[24],
        fontSize: size.spacing[24],
      },
    },
    _xlarge: {
      minWidth: size.spacing[64],
      fontSize: font.size.large,
      lineHeight: font.lineheight.xlarge,
      padding: `${size.spacing[12]} ${size.spacing[32]}`,
      '& svg, & i': {
        width: size.spacing[24],
        height: size.spacing[24],
        fontSize: size.spacing[24],
      },
    },
    // variants
    _ghost: {
      extend: [buttonVariant(theme, 'ghost'), focusWithPseudo()],
    },
    _primary: {
      extend: [buttonVariant(theme, 'primary'), focusWithPseudo()],
    },
    _secondary: {
      // same as ghost variant with a border
      extend: [buttonVariant(theme, 'ghost'), focusWithPseudo()],
      borderColor: color.getModalValues(color.border.interactive),
    },
    _success: {
      extend: [buttonVariant(theme, 'success'), focusWithPseudo()],
    },
    _warning: {
      extend: [buttonVariant(theme, 'warning'), focusWithPseudo()],
    },
    _error: {
      extend: [buttonVariant(theme, 'error'), focusWithPseudo()],
    },
  }
}
